/**
 * Submit a bug / Suggest a feature — a dead-simple in-app popup (NOT a browser
 * pop-out, so popup blockers never apply). Auto-fills the page + the signed-in
 * user. Optional: attach a screenshot/short clip (rides inline on the email) or
 * paste a link (Loom, Drive, …) for a longer recording. Posts multipart to
 * /feedback, which emails the report to support with the submitter as reply-to.
 */

import { useEffect, useState } from "react";
import { useApiClient } from "../lib/api-context.js";

type Kind = "bug" | "feature";

const COPY: Record<Kind, { title: string; cta: string; field: string; placeholder: string }> = {
  bug: {
    title: "Submit a bug",
    cta: "Send bug report",
    field: "What's the bug?",
    placeholder: "What went wrong? What were you trying to do, and what happened instead? Please explain here, or attach an image/video below (preferred) so we can see exactly what's going on.",
  },
  feature: {
    title: "Suggest a feature",
    cta: "Send suggestion",
    field: "What's your idea?",
    placeholder: "What would you like to see, and what would it help you do? Explain here, or attach an image/video below to show us what you mean.",
  },
};

/** Match the left-sidebar label exactly (route → nav label). */
const PAGE_LABELS: Record<string, string> = {
  "/dashboard": "Exec",
  "/marketing": "Marketing",
  "/products": "Products",
  "/cohorts": "Customer LTV",
  "/retention": "Retention",
  "/subscriptions": "Subscriptions",
  "/operations": "Operations",
  "/connections": "Connections",
  "/settings": "Settings",
  "/welcome": "Get set up",
  "/admin": "Clients",
};
function pageLabel(pathname: string): string {
  if (PAGE_LABELS[pathname]) return PAGE_LABELS[pathname];
  const seg = (pathname.split("/")[1] || "home").replace(/-/g, " ");
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

const MAX_ATTACH = 15 * 1024 * 1024;

export function FeedbackModal({
  kind,
  pathname,
  defaultName,
  defaultEmail,
  onClose,
}: {
  kind: Kind;
  pathname: string;
  defaultName?: string;
  defaultEmail?: string;
  onClose: () => void;
}): React.ReactElement {
  const api = useApiClient();
  const c = COPY[kind];

  const [name, setName] = useState(defaultName ?? "");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [page, setPage] = useState(pageLabel(pathname));
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape" && !submitting) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [submitting, onClose]);

  const onPickFile = (f: File | null): void => {
    setError(null);
    if (f && f.size > MAX_ATTACH) {
      setError("That file is too big to email (max 15 MB) — paste a link to it instead (Loom, Drive, etc.).");
      return;
    }
    setFile(f);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (!name.trim() || !email.trim() || !description.trim()) {
      setError("Please fill in your name, email, and a short description.");
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("type", kind);
      form.append("page", page);
      form.append("name", name.trim());
      form.append("email", email.trim());
      form.append("description", description.trim());
      if (file) form.append("attachment", file, file.name || "attachment");
      await api.postForm("/feedback", form);
      setDone(true);
      window.setTimeout(onClose, 1800);
    } catch (e) {
      const detail = (e as { detail?: { error?: string } })?.detail?.error;
      setError(detail ?? "Something went wrong sending that. Please try again.");
      setSubmitting(false);
    }
  };

  const label: React.CSSProperties = { fontSize: 12.5, fontWeight: 600, color: "var(--body)", display: "block", marginBottom: 5 };
  const input: React.CSSProperties = { width: "100%", padding: "9px 11px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 13.5, boxSizing: "border-box" };
  const chip: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 11px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", fontSize: 12.5, fontWeight: 600, cursor: "pointer" };

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div role="dialog" aria-label={c.title}
        style={{ width: 460, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--shadow-pop)", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>{c.title}</h2>
          <button onClick={onClose} aria-label="Close" disabled={submitting}
            style={{ border: "none", background: "transparent", color: "var(--body-faint)", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        {done ? (
          <div style={{ padding: "18px 4px", textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 6 }}>✅</div>
            <div style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}>Thank you — sent!</div>
            <div style={{ fontSize: 12.5, color: "var(--body)", marginTop: 4 }}>We'll get back to you at {email}.</div>
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div>
                <label style={label}>Your name</label>
                <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <label style={label}>Your email</label>
                <input style={input} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Page</label>
              <input style={input} value={page} onChange={(e) => setPage(e.target.value)} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>{c.field}</label>
              <textarea style={{ ...input, minHeight: 96, resize: "vertical", fontFamily: "inherit" }} value={description}
                onChange={(e) => setDescription(e.target.value)} placeholder={c.placeholder} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={label}>Attach a screenshot or video <span style={{ color: "var(--body-faint)", fontWeight: 400 }}>(optional)</span></label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <label style={chip}>
                  <input type="file" accept="image/*,video/*" style={{ display: "none" }}
                    onChange={(e) => onPickFile(e.target.files?.[0] ?? null)} />
                  📎 {file ? "Change file" : "Choose file"}
                </label>
                {file && (
                  <span style={{ fontSize: 12, color: "var(--body)" }}>
                    {file.name.slice(0, 26)} <button onClick={() => setFile(null)} style={{ border: "none", background: "none", color: "var(--accent)", cursor: "pointer" }}>remove</button>
                  </span>
                )}
              </div>
            </div>

            {error &&<div style={{ fontSize: 12.5, color: "var(--bad)", background: "color-mix(in srgb, var(--bad) 8%, transparent)", border: "1px solid color-mix(in srgb, var(--bad) 25%, var(--border))", borderRadius: 8, padding: "8px 11px", marginBottom: 12 }}>{error}</div>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button onClick={onClose} disabled={submitting} style={{ ...chip, fontWeight: 500 }}>Cancel</button>
              <button onClick={submit} disabled={submitting}
                style={{ ...chip, background: "var(--accent)", color: "#fff", borderColor: "var(--accent)", opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Sending…" : c.cta}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
